import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import {
  FileText,
  Plus,
  Edit,
  Eye,
  ChevronDown,
  ChevronUp,
  Shield,
  ShieldAlert,
} from "lucide-react";
import { AssessmentAudit, DataActAssessment, DataActResult } from "../types";
import { apiService } from "../services/api";
import LastModified from "./LastModified";
import StatusBadge from "./assessment/StatusBadge";

interface DataActAssessmentPanelProps {
  productId: string;
  canManageAssessment: boolean;
  /** Undefined when the product has no Data Act assessment. */
  audit?: AssessmentAudit | undefined;
}

export default function DataActAssessmentPanel({
  productId,
  canManageAssessment,
  audit,
}: DataActAssessmentPanelProps) {
  const navigate = useNavigate();
  const [showHistory, setShowHistory] = useState(false);

  const { data: latestRes, isLoading: latestLoading } = useQuery(
    ["dataActLatest", productId],
    () => apiService.getLatestDataActAssessment(productId),
    { enabled: !!productId },
  );

  const { data: allRes, isLoading: allLoading } = useQuery(
    ["dataActAssessments", productId],
    () => apiService.getDataActAssessmentsByProduct(productId),
    { enabled: !!productId && showHistory },
  );

  const latest: DataActAssessment | null = latestRes?.data?.data ?? null;
  const all: DataActAssessment[] = allRes?.data?.data ?? [];

  const parsedResult: DataActResult | null = (() => {
    if (!latest?.result) return null;
    try {
      return typeof latest.result === "string"
        ? JSON.parse(latest.result)
        : latest.result;
    } catch {
      return null;
    }
  })();

  const effectiveStatus: "not_started" | "draft" | "submitted" = latest
    ? (latest.status as "draft" | "submitted")
    : "not_started";

  const ctaLabel =
    effectiveStatus === "draft" ? "Continue Assessment" : "Start Assessment";

  const CtaIcon = effectiveStatus === "draft" ? Edit : Plus;

  const ctaOwnerOnly = true;

  const handleCta = () => {
    navigate(`/products/${productId}/data-act-assessment`);
  };

  return (
    <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          <h3 className="font-medium text-gray-900 dark:text-gray-100">
            Data Act Assessment
          </h3>
        </div>
        {latest && <StatusBadge status={effectiveStatus} />}
      </div>

      {latestLoading ? (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 mx-auto" />
        </div>
      ) : (
        <div className="space-y-3">
          {latest && (
            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <div className="flex justify-between">
                <span>Version</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  v{latest.version}
                </span>
              </div>
              {parsedResult && (
                <div className="flex justify-between">
                  <span>Result</span>
                  {latest?.status === "submitted" ? (
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          `/products/${productId}/data-act-assessment?view=${latest.id}`,
                        )
                      }
                      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full hover:ring-2 hover:ring-offset-1 hover:ring-primary-400 cursor-pointer transition-shadow ${
                        parsedResult.inScope
                          ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200"
                          : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200"
                      }`}
                      title="View assessment result"
                    >
                      {parsedResult.inScope ? (
                        <ShieldAlert className="h-3 w-3" />
                      ) : (
                        <Shield className="h-3 w-3" />
                      )}
                      {parsedResult.inScope ? "In Scope" : "Out of Scope"}
                    </button>
                  ) : (
                    <span
                      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                        parsedResult.inScope
                          ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200"
                          : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200"
                      }`}
                    >
                      {parsedResult.inScope ? (
                        <ShieldAlert className="h-3 w-3" />
                      ) : (
                        <Shield className="h-3 w-3" />
                      )}
                      {parsedResult.inScope ? "In Scope" : "Out of Scope"}
                    </span>
                  )}
                </div>
              )}
              <LastModified audit={audit} className="pt-1" />
            </div>
          )}

          {!ctaOwnerOnly || canManageAssessment ? (
            <button
              onClick={handleCta}
              className="btn btn-primary w-full text-sm"
            >
              <CtaIcon className="h-4 w-4 mr-1.5" />
              {ctaLabel}
            </button>
          ) : (
            <button
              disabled
              className="btn btn-primary w-full text-sm opacity-50 cursor-not-allowed"
              title="Only the product owner or a BA/division admin can start assessments"
            >
              <CtaIcon className="h-4 w-4 mr-1.5" />
              {ctaLabel}
            </button>
          )}

          {/* Version History Toggle */}
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors w-full justify-center pt-2 border-t border-gray-200 dark:border-gray-700"
          >
            {showHistory ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
            Version History
          </button>

          {showHistory && (
            <div className="overflow-x-auto">
              {allLoading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mx-auto" />
                </div>
              ) : all.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
                  No assessments yet
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Version
                      </th>
                      <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="text-right py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {all.map((assessment) => (
                      <tr key={assessment.id}>
                        <td className="py-2 pr-4 text-sm text-gray-900 dark:text-gray-100">
                          v{assessment.version}
                        </td>
                        <td className="py-2 pr-4">
                          <StatusBadge status={assessment.status} />
                        </td>
                        <td className="py-2 pr-4 text-sm text-gray-500 dark:text-gray-400">
                          {new Date(
                            assessment.completedAt || assessment.updatedAt,
                          ).toLocaleDateString()}
                        </td>
                        <td className="py-2 text-right">
                          <button
                            onClick={() =>
                              navigate(
                                `/products/${productId}/data-act-assessment?view=${assessment.id}`,
                              )
                            }
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          >
                            <Eye className="h-3 w-3" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
